/**
 * The tenancy routes of the route table (`routes.ts` spreads these into
 * `ROUTES`): who am I, the vendor's tenant registry, and a tenant admin's
 * users, groups and memberships (PLAN §11, D13, D14).
 *
 * All tenant-level: `permissionScope: 'tenant'`, so the permission must be held
 * through a tenant-wide group — `tenant.admin` for one studio does not make you
 * a tenant admin. A path id from another tenant answers `not-found`, the same
 * as one that does not exist (D13).
 *
 * Typed with `satisfies` against a type-only import, so `routes.ts` can import
 * this module without a runtime cycle and each entry keeps its concrete zod
 * types (`z.infer<typeof ROUTES.createGroup.body>` is the handler's input).
 */
import type { RouteDef } from './routes.js';
import {
  createdTenantResponseSchema,
  createGroupRequestSchema,
  createTenantRequestSchema,
  createUserRequestSchema,
  groupPathSchema,
  groupResponseSchema,
  groupWithMembersResponseSchema,
  listGroupsResponseSchema,
  listTenantsResponseSchema,
  listUsersResponseSchema,
  meResponseSchema,
  memberPathSchema,
  updateGroupRequestSchema,
  userPathSchema,
  userResponseSchema,
} from './tenancy.js';

export const TENANCY_ROUTES = {
  getMe: {
    id: 'getMe',
    method: 'GET',
    path: '/me',
    tag: 'tenancy',
    summary: 'Who am I',
    description: 'The caller, their tenant, every permission they hold, and where each applies (one grant per group).',
    params: null,
    query: null,
    body: null,
    response: meResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: ['not-found'],
  },
  createTenant: {
    id: 'createTenant',
    method: 'POST',
    path: '/tenants',
    tag: 'vendor',
    summary: 'Create a tenant',
    description:
      'Creates a tenant with its default groups (Admin, Producer/Editor, Operator, Viewer; D14) and its first admin, whose Cognito invitation is suppressed. All or nothing: `conflict` if the tenant id or the admin email exists. Vendor tenant only.',
    params: null,
    query: null,
    body: createTenantRequestSchema,
    response: createdTenantResponseSchema,
    successStatus: 201,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listTenants: {
    id: 'listTenants',
    method: 'GET',
    path: '/tenants',
    tag: 'vendor',
    summary: 'List tenants',
    description: 'Every tenant in this regional deployment (D12). Vendor tenant only.',
    params: null,
    query: null,
    body: null,
    response: listTenantsResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  createUser: {
    id: 'createUser',
    method: 'POST',
    path: '/users',
    tag: 'tenancy',
    summary: 'Create a user',
    description:
      'Creates a Cognito user in the caller’s tenant (the invitation is suppressed) and adds it to `groupIds`. Every group must exist in the tenant first; `conflict` if the email is taken anywhere in the pool.',
    params: null,
    query: null,
    body: createUserRequestSchema,
    response: userResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  listUsers: {
    id: 'listUsers',
    method: 'GET',
    path: '/users',
    tag: 'tenancy',
    summary: 'List users',
    description: 'The tenant’s users, by email.',
    params: null,
    query: null,
    body: null,
    response: listUsersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  deleteUser: {
    id: 'deleteUser',
    method: 'DELETE',
    path: '/users/{userId}',
    tag: 'tenancy',
    summary: 'Delete a user',
    description: 'Deletes the Cognito user and every membership; returns the deleted user. You cannot delete yourself, nor the last member of the Admin group (`conflict`).',
    params: userPathSchema,
    query: null,
    body: null,
    response: userResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  createGroup: {
    id: 'createGroup',
    method: 'POST',
    path: '/groups',
    tag: 'tenancy',
    summary: 'Create a group',
    description: 'A group grants `permissions` within `scope`: the whole tenant, or named sites and studios (D14). `vendor.*` permissions are grantable only in the vendor tenant.',
    params: null,
    query: null,
    body: createGroupRequestSchema,
    response: groupResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listGroups: {
    id: 'listGroups',
    method: 'GET',
    path: '/groups',
    tag: 'tenancy',
    summary: 'List groups',
    description: 'The tenant’s groups, by name.',
    params: null,
    query: null,
    body: null,
    response: listGroupsResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  getGroup: {
    id: 'getGroup',
    method: 'GET',
    path: '/groups/{groupId}',
    tag: 'tenancy',
    summary: 'Get a group',
    description: 'The group and its members’ user ids.',
    params: groupPathSchema,
    query: null,
    body: null,
    response: groupWithMembersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  updateGroup: {
    id: 'updateGroup',
    method: 'PATCH',
    path: '/groups/{groupId}',
    tag: 'tenancy',
    summary: 'Update a group',
    description:
      'Changes any of name, permissions, scope. The Admin group (and the vendor’s Vendor Admin group) must keep its admin permissions tenant-wide (`conflict`).',
    params: groupPathSchema,
    query: null,
    body: updateGroupRequestSchema,
    response: groupResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteGroup: {
    id: 'deleteGroup',
    method: 'DELETE',
    path: '/groups/{groupId}',
    tag: 'tenancy',
    summary: 'Delete a group',
    description: 'Deletes the group and its memberships; returns the deleted group. The Admin group cannot be deleted (`conflict`).',
    params: groupPathSchema,
    query: null,
    body: null,
    response: groupResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  addGroupMember: {
    id: 'addGroupMember',
    method: 'PUT',
    path: '/groups/{groupId}/members/{userId}',
    tag: 'tenancy',
    summary: 'Add a member',
    description: 'Idempotent. Both the group and the user must be in the caller’s tenant. Returns the group and its members.',
    params: memberPathSchema,
    query: null,
    body: null,
    response: groupWithMembersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  removeGroupMember: {
    id: 'removeGroupMember',
    method: 'DELETE',
    path: '/groups/{groupId}/members/{userId}',
    tag: 'tenancy',
    summary: 'Remove a member',
    description: 'Idempotent. The Admin group (and the Vendor Admin group) keeps at least one member (`conflict`). Returns the group and its members.',
    params: memberPathSchema,
    query: null,
    body: null,
    response: groupWithMembersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
} as const satisfies Record<string, RouteDef>;
